/**
 * Client-side signal collection for form submissions.
 *
 * Gathers the NON-security context the server cannot derive on its own
 * (screen/viewport, language, browser timezone, referrer, UTM, session id,
 * journey, timings, paste-vs-typed, JS/cookies flags). The server ALWAYS
 * derives the real IP/geo itself and never trusts anything sent from here.
 *
 * Installed once as a module side-effect: it patches history so SPA
 * navigations are recorded for the "pages viewed" / landing-page journey, and
 * attaches document listeners for paste + typing counts.
 */

import { getFirstTouch, getSessionTouch, isCurrentUtmMalformed } from '@/lib/growthIntel/tracker';

export interface ClientSignals {
  screen: string;
  viewport: string;
  language: string;
  timezone: string;
  referrer: string;
  landing_page: string;
  page_submitted: string;
  utm: Record<string, string>;
  first_touch: Record<string, unknown> | null;
  session_touch: Record<string, unknown> | null;
  utm_malformed: boolean;
  session_id: string;
  pages_viewed: number;
  time_on_page_ms: number;
  pasted: boolean;
  paste_count: number;
  typed_chars: number;
  js_enabled: boolean;
  cookies_enabled: boolean;
}

const SESSION_KEY = 'cendien_visit_sid';
const PAGES_KEY = 'cendien_visit_pages';
const LANDING_KEY = 'cendien_visit_landing';
const NAV_START_KEY = 'cendien_visit_start';
const PASTE_KEY = 'cendien_visit_pastes';
const TYPED_KEY = 'cendien_visit_typed';

function safeGet(key: string): string | null {
  try { return sessionStorage.getItem(key); } catch { return null; }
}
function safeSet(key: string, value: string): void {
  try { sessionStorage.setItem(key, value); } catch { /* ignore */ }
}

function uuid(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* ignore */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function ensureSession(): void {
  if (!safeGet(SESSION_KEY)) safeSet(SESSION_KEY, uuid());
  if (!safeGet(NAV_START_KEY)) safeSet(NAV_START_KEY, String(Date.now()));
}

/** Records a page view in the tab-scoped journey. */
export function trackPageView(path?: string): void {
  ensureSession();
  try {
    const p = path || (window.location.pathname + window.location.search);
    const raw = safeGet(PAGES_KEY);
    const pages: string[] = raw ? JSON.parse(raw) : [];
    const list = Array.isArray(pages) ? pages : [];
    if (list[list.length - 1] !== p) list.push(p);
    safeSet(PAGES_KEY, JSON.stringify(list.slice(-50)));
    if (!safeGet(LANDING_KEY)) safeSet(LANDING_KEY, p);
  } catch { /* ignore */ }
}

function bump(key: string, by = 1): void {
  const cur = Number(safeGet(key) || '0') || 0;
  safeSet(key, String(cur + by));
}

let installed = false;
function install(): void {
  if (installed || typeof window === 'undefined' || typeof document === 'undefined') return;
  installed = true;
  ensureSession();
  trackPageView(window.location.pathname + window.location.search);
  // Persist cross-session first touch as early as possible.
  try { getFirstTouch(); } catch { /* ignore */ }

  // Record SPA navigations by wrapping history.
  try {
    const wrap = (fn: History['pushState']) => function patched(this: History, ...args: unknown[]) {
      const result = (fn as (...a: unknown[]) => unknown).apply(this, args);
      try { trackPageView(window.location.pathname + window.location.search); } catch { /* ignore */ }
      return result;
    };
    window.history.pushState = wrap(window.history.pushState) as History['pushState'];
    window.history.replaceState = wrap(window.history.replaceState) as History['replaceState'];
  } catch { /* ignore */ }

  // Paste + typing counters (per tab).
  document.addEventListener('paste', (e) => {
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) bump(PASTE_KEY);
  }, true);
  document.addEventListener('keydown', (e) => {
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA') && e.key && e.key.length === 1) bump(TYPED_KEY);
  }, true);
}

/** True when cookies are usable (some strict/privacy modes block them). */
function cookiesEnabled(): boolean {
  try {
    document.cookie = 'cendien_ck=1; SameSite=Lax';
    return document.cookie.indexOf('cendien_ck=1') !== -1;
  } catch { return false; }
}

/** Collects the full client-side signal set for one submission. */
export function collectClientSignals(page?: string): ClientSignals {
  install();
  let utm: Record<string, string> = {};
  try {
    const params = new URLSearchParams(window.location.search);
    (['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const).forEach((k) => {
      const v = params.get(k);
      if (v) utm[k] = v.slice(0, 120);
    });
  } catch { /* ignore */ }

  let pages: string[] = [];
  try { pages = JSON.parse(safeGet(PAGES_KEY) || '[]'); } catch { pages = []; }
  if (!Array.isArray(pages)) pages = [];

  const pageSubmitted = page || (window.location.pathname + window.location.search);
  const start = Number(safeGet(NAV_START_KEY) || '0') || Date.now();

  return {
    screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    viewport: `${window.innerWidth || 0}x${window.innerHeight || 0}`,
    language: navigator.language || '',
    timezone: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { return ''; } })(),
    referrer: document.referrer ? document.referrer.slice(0, 300) : '',
    landing_page: safeGet(LANDING_KEY) || pageSubmitted,
    page_submitted: pageSubmitted,
    utm,
    first_touch: (() => { try { return getFirstTouch(); } catch { return null; } })(),
    session_touch: (() => { try { return getSessionTouch(); } catch { return null; } })(),
    utm_malformed: (() => { try { return isCurrentUtmMalformed(); } catch { return false; } })(),
    session_id: safeGet(SESSION_KEY) || '',
    pages_viewed: pages.length + (pages[pages.length - 1] === pageSubmitted ? 0 : 1),
    time_on_page_ms: Math.max(0, Date.now() - start),
    pasted: (Number(safeGet(PASTE_KEY) || '0') || 0) > 0,
    paste_count: Number(safeGet(PASTE_KEY) || '0') || 0,
    typed_chars: Number(safeGet(TYPED_KEY) || '0') || 0,
    js_enabled: true,
    cookies_enabled: cookiesEnabled(),
  };
}

// Install the listeners as soon as this module is evaluated (it is imported by
// every form through formSubmissions) so earlier pastes/typing are counted.
if (typeof window !== 'undefined') install();